// IDS evaluation core: pure, dependency-free predicates that decide
// whether an element satisfies an IDS facet.
//
// Everything here operates on an abstract `ElementView` (see below), the
// contract between this package and the host platform. The package never
// reads an IFC file itself; a host adapter builds `ElementView`s and
// feeds them in. See `idsAdapter.ts` for a source-agnostic adapter that
// only needs an `IfcSource`.
//
// Reference: https://github.com/buildingSMART/IDS

import type {
  Cardinality,
  Facet,
  IdsSpec,
  IdsValue,
} from './idsParse.js'
import { MESSAGES_EN, type IdsMessages } from './idsMessages.js'
import { attributeBaseType } from './ifcSchemaTypes.js'

// --- Abstract element view (the core knows nothing about IFC I/O) ----

export interface PropView {
  /** Nome do property set (ex.: Pset_WallCommon). */
  pset: string
  /** Nome da propriedade (ex.: FireRating). */
  name: string
  /** Valor em string; null quando a propriedade existe sem valor. */
  value: string | null
  /** Tipo IFC do valor (ex.: IFCLABEL), em MAIÚSCULAS; null se ausente. */
  dataType: string | null
}

export interface ClassificationView {
  /** Sistema de classificação (ex.: Uniclass 2015); null se ausente. */
  system: string | null
  /** Código / referência (ex.: EF_25_10); null se ausente. */
  value: string | null
}

export interface ContainerView {
  /** Classe IFC do contentor/agregador (ex.: IFCBUILDINGSTOREY). */
  ifcClass: string
  /** Relação (ex.: IFCRELAGGREGATES, IFCRELCONTAINEDINSPATIALSTRUCTURE). */
  relation: string
  /** predefinedType resolvido do contentor; null quando ausente. */
  predefinedType?: string | null
}

export interface ElementView {
  /** Classe IFC em MAIÚSCULAS (ex.: IFCWALL). */
  ifcClass: string
  globalId: string | null
  /** predefinedType efetivo: USERDEFINED resolvido para o nome real
   *  (ObjectType/ElementType/ProcessType) e herdado do tipo se ausente. */
  predefinedType: string | null
  name: string | null
  /** Literal do atributo PredefinedType antes de resolver USERDEFINED
   *  (ex.: 'USERDEFINED'). O facet aceita este OU o resolvido, porque o
   *  IDS permite verificar tanto 'USERDEFINED' como o nome atribuído. */
  predefinedTypeRaw?: string | null
  /** Lê um atributo por nome (Name, Description, Tag, ...); null quando
   *  ausente ou vazio. */
  attribute: (name: string) => string | null
  /** Lê um atributo distinguindo ausente (null) de presente-mas-vazio
   *  (''): o IDS trata um atributo vazio como falha, mas um atributo
   *  ausente ($) faz o opcional passar. Ausente na vista pura de teste
   *  (recai em `attribute`). */
  attributeRaw?: (name: string) => string | null
  properties: PropView[]
  classifications: ClassificationView[]
  /** Nomes dos materiais associados ao elemento. */
  materials: string[]
  /** Contentores/agregadores de que este elemento faz parte. */
  containers: ContainerView[]
}

// --- Núcleo puro -------------------------------------------------------

/** Tolerância de igualdade de vírgula flutuante do IDS.
 *
 *  A doc oficial (buildingSMART/IDS, Documentation/ImplementersDocumentation/
 *  tolerance.md) define, para a igualdade de doubles em ids:simpleValue e
 *  xs:restriction:
 *
 *    x == v  ⇒  (v - abs(v)·ε - ε) < x < (v + abs(v)·ε + ε),  ε = 1.0e-6
 *
 *  ou seja, uma banda simétrica com componente relativa (abs(v)·ε) e
 *  componente fixa (ε): |x - v| <= abs(v)·ε + ε. Combina os dois para
 *  cobrir números grandes (comprimento de via) e pequenos (área de fio).
 *  A doc NÃO aplica tolerância a intervalos (min/maxInclusive/Exclusive) -
 *  ver `matchValue`, onde os bounds usam comparação exata.
 *
 *  Os casos-fronteira da suite oficial colocam x EXATAMENTE no limite
 *  analítico da banda; a doc reconhece (nota do IEEE-754) que a precisão
 *  finita do double introduz falsos resultados no limite. O texto decimal
 *  das fixtures arredonda x, pelo que |x - v| fica ~1e-11 acima do limite
 *  no pior caso conforme (v=1). Absorvemos esse arredondamento com uma
 *  folga relativa de 1e-9 sobre o próprio limite: fica muito abaixo da
 *  granularidade da tolerância (1e-6) e bem acima do erro de
 *  representação (~1e-11), separando os casos pass (fronteira) dos fail
 *  (>1e-6 além). */
export const IDS_TOLERANCE_EPSILON = 1e-6
const IDS_TOLERANCE_BOUND_SLACK = 1e-9

export function numbersClose(actual: number, expected: number): boolean {
  const bound =
    IDS_TOLERANCE_EPSILON + IDS_TOLERANCE_EPSILON * Math.abs(expected)
  return Math.abs(actual - expected) <= bound * (1 + IDS_TOLERANCE_BOUND_SLACK)
}

/** Natureza do valor real do IFC, quando o adaptador a conhece.
 *
 *  - 'text': o valor é texto no IFC (IfcLabel, IfcIdentifier, IfcText,
 *    códigos de classificação, nomes de materiais). Compara-se letra a
 *    letra: "01" não é "1" e "21.10" não é "21.1".
 *  - 'number': o valor é numérico no IFC; aplica-se a tolerância.
 *  - 'any': desconhecida. Mantém o comportamento tolerante (número ou
 *    texto), que é o que uma vista sem tipos pode fazer. */
export type ValueKind = 'text' | 'number' | 'any'

/** Igualdade de valores tolerante a número vs string ("2.0" == "2") e à
 *  tolerância de vírgula flutuante do IDS (`numbersClose`). `a` é o valor
 *  esperado (do IDS), `b` o valor real (do IFC): a banda é relativa a `a`.
 *  Com `kind === 'text'` a comparação é exacta. */
function valuesEqual(a: string, b: string, kind: ValueKind = 'any'): boolean {
  if (a === b) return true
  if (kind === 'text') return false
  const na = Number(a)
  const nb = Number(b)
  if (
    a.trim() !== '' &&
    b.trim() !== '' &&
    Number.isFinite(na) &&
    Number.isFinite(nb)
  ) {
    return numbersClose(nb, na)
  }
  return false
}

/** Traduz o que o XSD tem e o JavaScript não: a subtracção de classes
 *  (`[a-z-[aeiou]]`) passa a lookahead negativo (`(?:(?![aeiou])[a-z])`).
 *  Só a forma não aninhada, que é a que aparece na prática. */
export function xsdPatternToJs(pattern: string): string {
  return pattern.replace(
    /\[((?:\\.|[^\]\\[])+)-\[((?:\\.|[^\]\\[])+)\]\]/g,
    (_, base: string, sub: string) => `(?:(?![${sub}])[${base}])`,
  )
}

const patternCache = new Map<string, RegExp | null>()

/** Compila um padrão XSD, ancorado à string inteira. Tenta primeiro com a
 *  flag `u` (sem ela o JavaScript lê `\p{Lu}` como o texto "p{Lu}"); se o
 *  modo Unicode recusar o padrão (escapes que o XSD aceita e o modo `u`
 *  não, como `\-` fora de uma classe), recai no modo clássico. null quando
 *  nenhum dos dois o aceita. */
function compilePattern(pattern: string): RegExp | null {
  const cached = patternCache.get(pattern)
  if (cached !== undefined) return cached
  const src = `^(?:${xsdPatternToJs(pattern)})$`
  let re: RegExp | null
  try {
    re = new RegExp(src, 'u')
  } catch {
    try {
      re = new RegExp(src)
    } catch {
      re = null
    }
  }
  patternCache.set(pattern, re)
  return re
}

/** Padrão XSD: ancorado à string inteira. Padrão inválido falha (não
 *  rebenta). */
function matchPattern(pattern: string, actual: string): boolean {
  const re = compilePattern(pattern)
  return re !== null && re.test(actual)
}

function hasConstraint(v: IdsValue): boolean {
  return (
    v.simpleValue !== undefined ||
    (v.enumeration !== undefined && v.enumeration.length > 0) ||
    v.pattern !== undefined ||
    v.minInclusive !== undefined ||
    v.maxInclusive !== undefined ||
    v.minExclusive !== undefined ||
    v.maxExclusive !== undefined ||
    v.length !== undefined ||
    v.minLength !== undefined ||
    v.maxLength !== undefined
  )
}

/** Um valor real (string) satisfaz uma restrição idsValue? Um idsValue
 *  sem restrições (objeto vazio) = só existência: qualquer valor
 *  não-nulo passa; um valor ausente (null) nunca passa uma restrição.
 *
 *  `kind` diz se o valor real é texto ou número no IFC (ver `ValueKind`).
 *  Facets cujo valor é sempre texto (classificação, material, entidade)
 *  passam 'text'; omitido, o comportamento é o tolerante de sempre. */
export function matchValue(
  v: IdsValue,
  actual: string | null,
  kind: ValueKind = 'any',
): boolean {
  if (actual === null) return false
  if (!hasConstraint(v)) return true
  if (
    v.simpleValue !== undefined &&
    !valuesEqual(v.simpleValue, actual, kind)
  ) {
    return false
  }
  if (
    v.enumeration !== undefined &&
    v.enumeration.length > 0 &&
    !v.enumeration.some((e) => valuesEqual(e, actual, kind))
  ) {
    return false
  }
  if (v.pattern !== undefined && !matchPattern(v.pattern, actual)) return false
  const num = Number(actual)
  const numeric = actual.trim() !== '' && Number.isFinite(num)
  if (v.minInclusive !== undefined && (!numeric || num < v.minInclusive)) {
    return false
  }
  if (v.maxInclusive !== undefined && (!numeric || num > v.maxInclusive)) {
    return false
  }
  if (v.minExclusive !== undefined && (!numeric || num <= v.minExclusive)) {
    return false
  }
  if (v.maxExclusive !== undefined && (!numeric || num >= v.maxExclusive)) {
    return false
  }
  if (v.length !== undefined && actual.length !== v.length) return false
  if (v.minLength !== undefined && actual.length < v.minLength) return false
  if (v.maxLength !== undefined && actual.length > v.maxLength) return false
  return true
}

/** Tipos IFC de valor que são texto: o valor compara-se letra a letra. */
const TEXT_VALUE_TYPES = new Set([
  'IFCLABEL',
  'IFCTEXT',
  'IFCIDENTIFIER',
  'IFCDESCRIPTIVEMEASURE',
  'IFCURIREFERENCE',
  'IFCDATE',
  'IFCDATETIME',
  'IFCTIME',
  'IFCDURATION',
  'IFCTEXTFONTNAME',
  'IFCPRESENTABLETEXT',
  'IFCGLOBALLYUNIQUEID',
])

/** Natureza do valor de uma propriedade, pelo tipo IFC que o embrulha. */
export function valueKindOfDataType(dataType: string | null): ValueKind {
  if (dataType === null) return 'any'
  return TEXT_VALUE_TYPES.has(dataType.toUpperCase()) ? 'text' : 'any'
}

/** Natureza do valor de um atributo, pelo tipo declarado no esquema
 *  (IFC4 e, se a classe só existir no IFC2X3, este). Texto e enumerados
 *  comparam-se letra a letra; o resto mantém a comparação tolerante. */
export function valueKindOfAttribute(
  ifcClass: string,
  attribute: string,
): ValueKind {
  const t =
    attributeBaseType(ifcClass, attribute, 'IFC4') ??
    attributeBaseType(ifcClass, attribute, 'IFC2X3')
  return t === 'string' || t === 'enum' ? 'text' : 'any'
}

/** Descrição legível de uma restrição, para as mensagens do relatório. */
function describeValue(v: IdsValue, m: IdsMessages): string {
  if (v.simpleValue !== undefined) return `"${v.simpleValue}"`
  if (v.enumeration && v.enumeration.length > 0) {
    return m.oneOf(v.enumeration.join(', '))
  }
  if (v.pattern !== undefined) return m.pattern(v.pattern)
  const parts: string[] = []
  if (v.minInclusive !== undefined) parts.push(`>= ${v.minInclusive}`)
  if (v.maxInclusive !== undefined) parts.push(`<= ${v.maxInclusive}`)
  if (v.minExclusive !== undefined) parts.push(`> ${v.minExclusive}`)
  if (v.maxExclusive !== undefined) parts.push(`< ${v.maxExclusive}`)
  if (v.length !== undefined) parts.push(m.length(v.length))
  if (v.minLength !== undefined) parts.push(m.minLength(v.minLength))
  if (v.maxLength !== undefined) parts.push(m.maxLength(v.maxLength))
  return parts.length > 0 ? parts.join(m.and) : m.anyValue
}

export interface FacetCheck {
  /** false quando o facet não é avaliável nesta fase (unsupported). */
  supported: boolean
  /** Razão da falha; null quando passa (ou não avaliado). */
  reason: string | null
}

const PASS: FacetCheck = { supported: true, reason: null }
const unsupported = (): FacetCheck => ({ supported: false, reason: null })

/** Aplica a lógica de cardinalidade a um par (presente, corresponde). */
function cardinalityVerdict(
  cardinality: Cardinality,
  present: boolean,
  matches: boolean,
  label: string,
  expected: string,
  m: IdsMessages,
): FacetCheck {
  // Proibido é o contrário do obrigatório: falha só quando o facet
  // INTEIRO corresponde (existe E bate o valor/sistema pedido). Um
  // elemento com outro valor não viola "é proibido o valor B". Mesma
  // semântica do ifctester (IfcOpenShell), a implementação de referência.
  if (cardinality === 'prohibited') {
    return present && matches
      ? { supported: true, reason: m.mustNotBePresent(label) }
      : PASS
  }
  // required (default) e optional partilham: se ausente, required falha e
  // optional passa; se presente, tem de corresponder.
  if (!present) {
    return cardinality === 'optional'
      ? PASS
      : { supported: true, reason: m.isMissing(label, expected) }
  }
  return matches
    ? PASS
    : { supported: true, reason: m.doesNotMatch(label, expected) }
}

/** Um contentor satisfaz um facet partOf? Verifica a relação, a classe
 *  da entidade contentora e (quando indicado) o seu predefinedType. */
function partOfMatches(
  facet: Extract<Facet, { type: 'partOf' }>,
  c: ContainerView,
): boolean {
  if (
    facet.relation !== undefined &&
    c.relation.toUpperCase() !== facet.relation.toUpperCase()
  ) {
    return false
  }
  if (facet.entity === undefined) return true
  if (!matchValue(facet.entity.name, c.ifcClass)) return false
  if (
    facet.entity.predefinedType !== undefined &&
    !matchValue(facet.entity.predefinedType, c.predefinedType ?? null)
  ) {
    return false
  }
  return true
}

/** O predefinedType da vista satisfaz a restrição do facet? Aceita o
 *  valor resolvido (USERDEFINED -> nome atribuído) ou o literal cru
 *  ('USERDEFINED'), pois o IDS permite verificar qualquer um. */
function predefinedTypeMatches(fv: IdsValue, view: ElementView): boolean {
  if (matchValue(fv, view.predefinedType)) return true
  const raw = view.predefinedTypeRaw
  if (raw !== undefined && raw !== null && raw !== view.predefinedType) {
    return matchValue(fv, raw)
  }
  return false
}

/** Avalia um facet de REQUISITO contra um elemento.
 *
 *  `m` escolhe a língua das razões de falha; a decisão de passar ou
 *  falhar não depende dela. */
export function checkRequirement(
  facet: Facet,
  view: ElementView,
  m: IdsMessages = MESSAGES_EN,
): FacetCheck {
  const card: Cardinality = facet.cardinality ?? 'required'
  switch (facet.type) {
    case 'entity': {
      const matches =
        matchValue(facet.name, view.ifcClass) &&
        (facet.predefinedType === undefined ||
          predefinedTypeMatches(facet.predefinedType, view))
      return matches
        ? PASS
        : {
            supported: true,
            reason: m.entityMismatch(describeValue(facet.name, m)),
          }
    }
    case 'attribute': {
      if (facet.name.simpleValue === undefined) return unsupported()
      const attrName = facet.name.simpleValue
      // Distingue ausente ($ -> null) de presente-mas-vazio ('' -> '').
      // Um atributo vazio existe (o opcional não escapa) mas nunca
      // satisfaz o valor nem a existência ("fails if empty").
      const raw = view.attributeRaw
        ? view.attributeRaw(attrName)
        : view.attribute(attrName)
      const present = raw !== null
      const nonEmpty = raw !== null && raw !== ''
      const matches =
        facet.value === undefined
          ? nonEmpty
          : matchValue(
              facet.value,
              raw,
              valueKindOfAttribute(view.ifcClass, attrName),
            )
      const expected = facet.value
        ? describeValue(facet.value, m)
        : m.toBePresent
      return cardinalityVerdict(
        card,
        present,
        matches,
        m.attribute(attrName),
        expected,
        m,
      )
    }
    case 'property': {
      // dataType tolerante: quando não conseguimos determinar o tipo real
      // (p.dataType === null) não falhamos por isso; só falha um tipo
      // conhecido que difira do esperado.
      const dataTypeOk = (actual: string | null) =>
        facet.dataType === undefined ||
        actual === null ||
        actual.toUpperCase() === facet.dataType.toUpperCase()
      // Uma célula de valor satisfaz? Sem restrição de valor, a existência
      // exige um valor real (não-null: '' e UNKNOWN já viram null).
      const valueOk = (p: PropView) =>
        dataTypeOk(p.dataType) &&
        (facet.value === undefined
          ? p.value !== null
          : matchValue(facet.value, p.value, valueKindOfDataType(p.dataType)))
      const candidates = view.properties.filter(
        (p) =>
          matchValue(facet.propertySet, p.pset) &&
          matchValue(facet.baseName, p.name),
      )
      const present = candidates.length > 0
      // Cardinalidade "todos os que correspondem satisfazem": TODOS os
      // property sets que correspondem ao propertySet têm de satisfazer;
      // dentro de cada um, TODAS as propriedades cujo nome corresponde ao
      // baseName têm de satisfazer; para cada propriedade, QUALQUER dos
      // seus valores (bounded/list) que satisfaça basta.
      const matchingPsets = [
        ...new Set(
          view.properties
            .filter((p) => matchValue(facet.propertySet, p.pset))
            .map((p) => p.pset),
        ),
      ]
      const psetSatisfies = (psetName: string): boolean => {
        const names = [
          ...new Set(
            view.properties
              .filter(
                (p) => p.pset === psetName && matchValue(facet.baseName, p.name),
              )
              .map((p) => p.name),
          ),
        ]
        if (names.length === 0) return false
        return names.every((nm) =>
          view.properties.some(
            (p) => p.pset === psetName && p.name === nm && valueOk(p),
          ),
        )
      }
      const matches =
        matchingPsets.length > 0 && matchingPsets.every(psetSatisfies)
      const label = m.property(
        describeValue(facet.propertySet, m),
        describeValue(facet.baseName, m),
      )
      const expectedParts: string[] = []
      if (facet.value) expectedParts.push(describeValue(facet.value, m))
      if (facet.dataType) expectedParts.push(m.dataType(facet.dataType))
      const expected =
        expectedParts.length > 0 ? expectedParts.join(', ') : m.toBePresent
      return cardinalityVerdict(card, present, matches, label, expected, m)
    }
    case 'classification': {
      // Existência = o elemento tem alguma classificação; corresponde =
      // há uma que bate sistema E código. Separá-los faz o opcional
      // falhar quando existe classificação mas nenhuma corresponde.
      const present = view.classifications.length > 0
      const matches = view.classifications.some(
        (c) =>
          (facet.system === undefined ||
            matchValue(facet.system, c.system, 'text')) &&
          (facet.value === undefined ||
            matchValue(facet.value, c.value, 'text')),
      )
      const bits: string[] = []
      if (facet.system) bits.push(m.system(describeValue(facet.system, m)))
      if (facet.value) bits.push(m.code(describeValue(facet.value, m)))
      return cardinalityVerdict(
        card,
        present,
        matches,
        m.classification,
        bits.length > 0 ? bits.join(', ') : m.toBePresent,
        m,
      )
    }
    case 'material': {
      const present = view.materials.length > 0
      const matches =
        facet.value === undefined
          ? present
          : view.materials.some((m) => matchValue(facet.value!, m, 'text'))
      return cardinalityVerdict(
        card,
        present,
        matches,
        m.material,
        facet.value ? describeValue(facet.value, m) : m.toBePresent,
        m,
      )
    }
    case 'partOf': {
      const present = view.containers.some((c) => partOfMatches(facet, c))
      const expected = facet.entity
        ? m.partOfEntity(describeValue(facet.entity.name, m))
        : m.partOfAnything
      return cardinalityVerdict(card, present, present, m.partOf, expected, m)
    }
    default:
      return unsupported()
  }
}

/** Um facet de APPLICABILITY seleciona (positivamente) um elemento? */
function applicabilityFacetMatches(facet: Facet, view: ElementView): boolean {
  switch (facet.type) {
    case 'entity':
      return (
        matchValue(facet.name, view.ifcClass) &&
        (facet.predefinedType === undefined ||
          predefinedTypeMatches(facet.predefinedType, view))
      )
    case 'attribute': {
      if (facet.name.simpleValue === undefined) return true // não filtra
      const actual = view.attribute(facet.name.simpleValue)
      if (actual === null) return false
      return facet.value === undefined
        ? true
        : matchValue(
            facet.value,
            actual,
            valueKindOfAttribute(view.ifcClass, facet.name.simpleValue),
          )
    }
    case 'property': {
      const cands = view.properties.filter(
        (p) =>
          matchValue(facet.propertySet, p.pset) &&
          matchValue(facet.baseName, p.name),
      )
      if (cands.length === 0) return false
      return facet.value === undefined
        ? true
        : cands.some((p) =>
            matchValue(facet.value!, p.value, valueKindOfDataType(p.dataType)),
          )
    }
    case 'classification':
      return view.classifications.some(
        (c) =>
          (facet.system === undefined ||
            matchValue(facet.system, c.system, 'text')) &&
          (facet.value === undefined ||
            matchValue(facet.value, c.value, 'text')),
      )
    case 'material':
      return facet.value === undefined
        ? view.materials.length > 0
        : view.materials.some((m) => matchValue(facet.value!, m, 'text'))
    case 'partOf':
      return view.containers.some((c) => partOfMatches(facet, c))
    default:
      return true // facet realmente desconhecido: não exclui
  }
}

/** Todos os facets de applicability batem certo neste elemento? */
export function applicabilityMatches(
  facets: Facet[],
  view: ElementView,
): boolean {
  return facets.every((f) => applicabilityFacetMatches(f, view))
}

// --- Resultado ---------------------------------------------------------

export interface ElementFailure {
  expressID: number
  globalId: string | null
  ifcClass: string
  name: string | null
  reasons: string[]
}

export interface SpecResult {
  spec: IdsSpec
  /** ok = todos passam; has-failures = há falhas; unresolved = a
   *  applicability não pôde ser resolvida a um conjunto de elementos. */
  status: 'ok' | 'has-failures' | 'unresolved'
  applicableCount: number
  passCount: number
  /** expressIDs dos elementos aplicáveis que passaram (para pintar a
   *  verde no 3D). */
  passExpressIDs: number[]
  failures: ElementFailure[]
  /** Requisitos não avaliados nesta fase (ex.: classification/material). */
  unsupportedRequirements: string[]
  note?: string
}

export interface IdsReport {
  results: SpecResult[]
}

// --- Relatório: cores 3D e CSV ----------------------------------------

const COLOR_PASS = '#22c55e'
const COLOR_FAIL = '#ef4444'

/** Mapa de cores para o 3D: elementos que passam a verde, que falham a
 *  vermelho (o vermelho ganha, aplicado por cima). Um elemento que
 *  falha numa spec e passa noutra fica vermelho (há um problema). */
export function buildIdsColorMap(
  report: IdsReport,
  modelId: string,
): Map<string, Map<number, string>> {
  const inner = new Map<number, string>()
  for (const r of report.results) {
    for (const id of r.passExpressIDs) {
      if (!inner.has(id)) inner.set(id, COLOR_PASS)
    }
  }
  for (const r of report.results) {
    for (const f of r.failures) inner.set(f.expressID, COLOR_FAIL)
  }
  const byModel = new Map<string, Map<number, string>>()
  byModel.set(modelId, inner)
  return byModel
}

export interface BcfTopicDraft {
  title: string
  description: string
}

/** Constrói um rascunho de tópico BCF por especificação com falhas: o
 *  título resume a spec e a descrição lista os elementos falhados
 *  (classe, nome, GlobalId, razão). Pure; a criação no módulo BCF é do
 *  painel. Limita a 200 elementos por tópico para não gerar descrições
 *  enormes. */
export function buildBcfTopicDrafts(
  report: IdsReport,
  m: IdsMessages = MESSAGES_EN,
): BcfTopicDraft[] {
  const CAP = 200
  const out: BcfTopicDraft[] = []
  for (const r of report.results) {
    if (r.status !== 'has-failures' || r.failures.length === 0) continue
    const n = r.failures.length
    const lines = r.failures
      .slice(0, CAP)
      .map(
        (f) =>
          `- ${f.ifcClass}${f.name ? ' ' + f.name : ''}` +
          `${f.globalId ? ' [' + f.globalId + ']' : ''}: ${f.reasons.join('; ')}`,
      )
    const more = n > CAP ? m.bcfMore(n - CAP) : ''
    out.push({
      title: m.bcfTitle(r.spec.name ?? 'spec', n),
      description:
        `${m.bcfNotMet(n, r.applicableCount)}\n\n${lines.join('\n')}${more}`,
    })
  }
  return out
}

function csvCell(s: string): string {
  return /[",\r\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Relatório de falhas em CSV (abre no Excel). */
export function idsToCsv(
  report: IdsReport,
  m: IdsMessages = MESSAGES_EN,
): string {
  const rows: string[][] = [[...m.csvHeader]]
  for (const r of report.results) {
    if (r.status === 'unresolved') {
      rows.push([r.spec.name ?? '', m.csvNotEvaluated, '', '', '', r.note ?? ''])
      continue
    }
    for (const f of r.failures) {
      rows.push([
        r.spec.name ?? '',
        m.csvFail,
        f.globalId ?? '',
        f.ifcClass,
        f.name ?? '',
        f.reasons.join('; '),
      ])
    }
  }
  return rows.map((cols) => cols.map(csvCell).join(',')).join('\r\n')
}
